/* eslint-disable react/jsx-props-no-spreading */
import React from 'react'
import {
  Button,
  Grid,
  Modal,
  Spacer,
  Input,
  Text,
  useMediaQuery,
} from '@geist-ui/react'
import ReCAPTCHA from 'react-google-recaptcha'
import { Controller } from 'react-hook-form'
import InputMask from 'react-input-mask'
import AddressAutocomplete from '../AddressAutocomplete'
import { InputError } from 'components'
import EnrollmentHeader from '../EnrollmentHeader'

export default function StepAEnrollmentForm({
  register,
  errors,
  control,
  handleSubmit,
  onSubmit,
  isLoading,
  isUsed,
  setIsUsed,
  isCaptchaComplete,
  setIsCaptchaComplete,
  isAcknowledged,
  setIsAcknowledged,
  isConsentGiven,
  setIsConsentGiven,
  captchaRef,
}) {
  const isSM = useMediaQuery('sm', { match: 'up' })
  const isMD = useMediaQuery('md', { match: 'up' })

  const emailField = register('email', {
    required: 'This field cannot be blank.',
  })

  return (
    <form style={{ width: '100%' }} onSubmit={handleSubmit(onSubmit)}>
      <EnrollmentHeader
        title="Enroll Today!"
        description={
          <>
            Complete the form below using the contact information and service
            address in which you desire coverage. Please note:{' '}
            <strong>Only residential accounts are eligible at this time.</strong>
          </>
        }
      />
      <Grid.Container gap={0.75}>
        <Grid direction="column" xs={24} sm={12}>
          <Input
            className="white-input"
            placeholder="First Name"
            type={(errors.first_name && 'error') || 'secondary'}
            {...register('first_name', {
              required: 'This field cannot be blank.',
            })}
            width="100%"
          />
          <InputError name="first_name" error={errors} />
        </Grid>
        {!isSM && <Spacer h={1} />}
        <Grid direction="column" xs={24} sm={12}>
          <Input
            className="white-input"
            placeholder="Last Name"
            type={(errors.last_name && 'error') || 'secondary'}
            {...register('last_name', {
              required: 'This field cannot be blank.',
            })}
            width="100%"
          />
          <InputError name="last_name" error={errors} />
        </Grid>
      </Grid.Container>

      <Spacer h={1} />

      <Grid.Container gap={1}>
        <Grid direction="column" xs={24} style={{ postion: 'relative' }}>
          <Controller
            name="address"
            control={control}
            rules={{ required: 'This field cannot be blank.' }}
            render={({ field }) => (
              <AddressAutocomplete
                field={field}
                error={errors.address}
                placeholder="Street Address"
              />
            )}
          />
          <InputError name="address" error={errors} />
          <Text
            p
            font="14px"
            style={{ color: '#6b7280', marginTop: '6px' }}
          >
            Use the service address where coverage should apply.
          </Text>
        </Grid>
      </Grid.Container>

      <Spacer h={1} />

      <Grid.Container gap={0.75}>
        <Grid direction="column" xs={24} sm={12}>
          {' '}
          <InputMask
            {...register('phone_number', {
              required: 'This field cannot be blank.',
            })}
            type={(errors.phone_number && 'error') || 'secondary'}
            mask="(999) 999-9999"
            width="100%"
          >
            {(inputProps) => (
              <Input
                className="white-input"
                placeholder="Phone Number"
                {...inputProps}
              />
            )}
          </InputMask>
          <InputError name="phone_number" error={errors} />
        </Grid>
        {!isSM && <Spacer h={1} />}
        <Grid direction="column" xs={24} sm={12}>
          <Input
            className="white-input"
            placeholder="Email Address"
            type={isUsed || errors.email ? 'error' : 'secondary'}
            {...emailField}
            width="100%"
            onChange={(e) => {
              emailField.onChange(e)
              isUsed && setIsUsed(false)
            }}
          />
          <InputError name="email" error={errors} />
        </Grid>
      </Grid.Container>

      <Spacer h={1.75} />

      <Grid.Container>
        <Grid direction="column" xs={24}>
          <Input
            className="white-input"
            placeholder="Account Number (Optional)"
            type={(errors.account_number && 'error') || 'secondary'}
            {...register('account_number')}
            width="100%"
          />
        </Grid>
      </Grid.Container>

      <Spacer h={2} />

      <Grid.Container gap={2} direction="column">
        <Grid justify="center">
          <input
            required
            {...register('consent')}
            id="consent"
            aria-describedby="consent"
            name="consent"
            type="checkbox"
            checked={isConsentGiven}
            onChange={() => setIsConsentGiven(!isConsentGiven)}
          />
          <label
            htmlFor="consent"
            style={{
              marginLeft: '10px',
              opacity: '70%',
              fontSize: '14px',
            }}
          >
            I acknowledge that by enrolling in WaterPro Water Leak Protection
            Plan℠, I consent to receive communications regarding my water
            service.
          </label>
        </Grid>
        <Grid justify="center">
          <input
            required
            {...register('guidelines')}
            id="guidelines"
            aria-describedby="guidelines"
            name="guidelines"
            type="checkbox"
            checked={isAcknowledged}
            onChange={() => setIsAcknowledged(!isAcknowledged)}
          />
          <label
            htmlFor="guidelines"
            style={{
              marginLeft: '10px',
              opacity: '70%',
              fontSize: '14px',
            }}
          >
            I acknowledge that I have reviewed the Program Guidelines. View the
            Program Guidelines{' '}
            <a
              href="https://waterproplan.com/wp-content/uploads/2021/11/WaterPro-Guidelines_11_5.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </a>
            .
          </label>
        </Grid>
        <Grid justify="center">
          <ReCAPTCHA
            sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
            ref={captchaRef}
            onChange={() => setIsCaptchaComplete(!isCaptchaComplete)}
          />
        </Grid>
      </Grid.Container>

      <Spacer h={1} />

      <Grid.Container justify="start" width="90%" gap={2}>
        <Grid xs={24} md={16}>
          <Button
            className="enroll-button"
            loading={isLoading}
            htmlType="submit"
            type="success"
            disabled={!isCaptchaComplete || !isAcknowledged || !isConsentGiven}
            style={{
              backgroundColor: 'rgb(87, 179, 228)',
              border: '1px solid rgb(0, 64, 128)',
              borderRadius: '4px',
              color: 'white',
              fontSize: '18px',
              height: '50px',
              minWidth: '181px',
            }}
          >
            Enroll now
          </Button>
        </Grid>
      </Grid.Container>
    </form>
  )
}
